import { ApiError } from '../../shared/api/errors';
import type { TFolder, TFolderCrumb, TFolderScope } from './api';

export const SCOPE_ROOT_LABEL: Record<TFolderScope, string> = {
  flows: 'Flows',
  library: 'Library',
};

export const FOLDER_MAX_DEPTH = 5;

// A list asked for a folder that is gone: the folder browser says so, the list need not.
export const isFolderGone = (error: unknown): boolean =>
  error instanceof ApiError && error.code === 'folders.not_found';

export const childFolders = (folders: readonly TFolder[], parentId: string | null): TFolder[] =>
  folders
    .filter((folder) => folder.parentId === parentId)
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));

export const pathText = (path: readonly TFolderCrumb[]): string =>
  path.map((crumb) => crumb.name).join(' / ');

export const subtreeIds = (folders: readonly TFolder[], id: string): Set<string> => {
  const found = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const folder of folders) {
      if (folder.parentId && found.has(folder.parentId) && !found.has(folder.id)) {
        found.add(folder.id);
        grew = true;
      }
    }
  }
  return found;
};

/** Levels from this folder to its deepest descendant, itself included. */
export const subtreeHeight = (folders: readonly TFolder[], id: string): number => {
  const inside = subtreeIds(folders, id);
  const base = folders.find((folder) => folder.id === id)?.depth ?? 1;
  const deepest = Math.max(
    ...folders.filter((folder) => inside.has(folder.id)).map((folder) => folder.depth),
  );
  return deepest - base + 1;
};

export const countLine = (folder: Pick<TFolder, 'itemCount' | 'folderCount'>, noun: string) => {
  const parts = [
    folder.folderCount > 0
      ? `${folder.folderCount.toLocaleString('en-US')} ${folder.folderCount === 1 ? 'folder' : 'folders'}`
      : null,
    `${folder.itemCount.toLocaleString('en-US')} ${folder.itemCount === 1 ? noun : `${noun}s`}`,
  ];
  return parts.filter(Boolean).join(' · ');
};
